import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Box, Stack, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useEffect } from "react";
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import Block from "@/components/Block";
import CustomCalendarHeader from "@/components/CustomCalendarHeader";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import { getGuestPersons } from "@/utils/booking";
import type { BookingFormData } from "../Details";

export default function GuestInfoForm() {
  const { state } = useBookingContext();
  const { data: campgroundData } = useCampground();
  const { fields } = useFieldArray({
    name: "guests",
  });
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext<BookingFormData>();

  const name = useWatch({ control, name: "name" });
  const lastname = useWatch({ control, name: "lastname" });

  useEffect(() => {
    setValue("guests.0.first_name", name);
    setValue("guests.0.last_name", lastname);
  }, [name, lastname, setValue]);

  if (fields.length === 0) {
    return null;
  }

  const guestPersons = getGuestPersons(state.selectedPersons, campgroundData.campground.persons);

  const getGuestLabel = (index: number) => {
    const person = guestPersons[index]!;
    const count = guestPersons.slice(0, index + 1).filter((p) => p.id === person.id).length;
    return `${person.name} ${count}`;
  };

  return (
    <Block flushOnMobile>
      <Typography variant="h3" sx={{ mb: 2 }}>
        <Trans>Guest Information</Trans>
      </Typography>
      <Stack spacing={2}>
        {fields.map((field, index) => {
          return (
            <Stack key={field.id} spacing={2}>
              <Typography variant="body1" gutterBottom>
                {getGuestLabel(index)}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Controller
                  name={`guests.${index}.first_name`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t`First Name`}
                      sx={{ flex: 1 }}
                      variant="filled"
                      required
                      error={!!errors.guests?.[index]?.first_name}
                      helperText={errors.guests?.[index]?.first_name?.message}
                    />
                  )}
                />
                <Controller
                  name={`guests.${index}.last_name`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t`Last Name`}
                      sx={{ flex: 1 }}
                      variant="filled"
                      required
                      error={!!errors.guests?.[index]?.last_name}
                      helperText={errors.guests?.[index]?.last_name?.message}
                    />
                  )}
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Controller
                  name={`guests.${index}.birthdate`}
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label={t`Birthdate`}
                      onAccept={() => field.onBlur()}
                      sx={{ flex: 1 }}
                      openTo="year"
                      views={["year", "month", "day"]}
                      maxDate={new Date()}
                      format="dd.MM.yyyy"
                      closeOnSelect
                      slotProps={{
                        textField: {
                          error: !!errors.guests?.[index]?.birthdate?.message,
                          helperText: errors.guests?.[index]?.birthdate?.message,
                          variant: "filled",
                          required: true,
                          sx: { flex: 1 },
                        },
                        toolbar: { hidden: true },
                        actionBar: { actions: [] },
                      }}
                      slots={{
                        calendarHeader: CustomCalendarHeader,
                      }}
                    />
                  )}
                />
                <Box sx={{ flex: 1, display: { xs: "none", sm: "block" } }} />
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    </Block>
  );
}
